import api from '../config/api';

const paymentService = {
  // Calculate order pricing (preview)
  calculatePricing: async (items, couponCode = null) => {
    try {
      const response = await api.post('/payments/calculate-pricing', {
        items: items.map(item => ({
          productId: item.product._id,
          quantity: item.quantity
        })),
        couponCode
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في حساب الأسعار';
    }
  },

  // Create payment for order
  createPayment: async (orderData) => {
    try {
      const response = await api.post('/payments/create-payment', orderData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في إنشاء عملية الدفع';
    }
  },

  // Confirm payment after Moyasar callback
  confirmPayment: async (orderId, paymentId) => {
    try {
      const response = await api.post('/payments/confirm', {
        orderId,
        paymentId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في تأكيد عملية الدفع';
    }
  },

  // Get payment status
  getPaymentStatus: async (orderId) => {
    try {
      const response = await api.get(`/payments/status/${orderId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في الحصول على حالة الدفع';
    }
  },

  // Load Moyasar script dynamically
  loadMoyasarScript: () => {
    return new Promise((resolve, reject) => {
      // Check if script already loaded
      if (window.Moyasar) {
        resolve(window.Moyasar);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.moyasar.com/mpf/1.10.0/moyasar.js';
      script.async = true;
      script.onload = () => resolve(window.Moyasar);
      script.onerror = () => reject(new Error('Failed to load Moyasar script'));
      document.head.appendChild(script);
    });
  },

  // Ensure Moyasar CSS is loaded to avoid distorted UI
  ensureMoyasarStyles: () => {
    if (document.querySelector('link[data-moyasar="css"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.moyasar.com/mpf/1.10.0/moyasar.css';
    link.setAttribute('data-moyasar', 'css');
    document.head.appendChild(link);
  },

  // Initialize Moyasar payment form
  initializeMoyasar: async (elementSelector, options) => {
    try {
      await paymentService.loadMoyasarScript();
      paymentService.ensureMoyasarStyles();
      
      // Configure default handlers if not provided
      const defaultOptions = {
        language: 'ar',
        // اترك redirect افتراضياً (false) إلا إذا أرسلت الخيار من المستدعي
        manual_payment: false,
        on_failure: (error) => {
          console.error('Moyasar payment failed:', error);
        }
      };
      
      // Initialize Moyasar with the provided options
      window.Moyasar.init({
        element: elementSelector,
        ...defaultOptions,
        ...options
      });
    } catch (error) {
      console.error('Failed to initialize Moyasar:', error);
      throw new Error('فشل في تحميل نموذج الدفع');
    }
  },

  // Upload bank transfer receipt
  uploadBankReceipt: async (orderId, receiptFile) => {
    try {
      const formData = new FormData();
      formData.append('orderId', orderId);
      formData.append('receipt', receiptFile);

      const response = await api.post('/payments/upload-bank-receipt', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في رفع إيصال التحويل';
    }
  },

  // Confirm bank transfer (Admin only)
  confirmBankTransfer: async (orderId) => {
    try {
      const response = await api.post('/payments/confirm-bank-transfer', {
        orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في تأكيد التحويل البنكي';
    }
  },

  // Reject bank transfer (Admin only)
  rejectBankTransfer: async (orderId, reason) => {
    try {
      const response = await api.post('/payments/reject-bank-transfer', {
        orderId,
        reason
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'فشل في رفض التحويل البنكي';
    }
  }
};

export default paymentService;
